/* eslint-disable */
import { CircularProgress, Tooltip } from "@mui/material";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import { SnackbarOrigin } from "@mui/material/Snackbar";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import { styled } from "@mui/material/styles";
import { visuallyHidden } from "@mui/utils";
import * as React from "react";
import { useRef } from "react";
import { connect, useSelector, type ConnectedProps } from "react-redux";
import AuthError from "../../../../components/ErrorComponents/AuthError";
import GenericModal from "../../../../components/Modal";
import Pagination from "../../../../components/TablePagination";
import LanguageStrings from "../../../../i18n/locales";
import { type RootState } from "../../../../store";
import { deviceDeleteCampaign, fetchDeviceInfo, fetchDeviceList, updateDevice, updateDevicePolling } from "../../redux/actionCreators";
import { DeviceListState } from "../../redux/deviceListSlice";
import { APIConfiguration } from '../../../../utils/apiConfiguration'
import { useNavigate } from "react-router-dom";
import ViewDevice from '../ViewDevice/ViewDevice'
import { getOperationPermissions } from '../../../dashboard/redux/actionCreator';
import { type OperationPermissionState } from '../../../dashboard/redux/operationPermissionSlice'
import { useAuth } from 'react-oidc-context'
import useRefreshHamburgNotifier from '../../../../customHooks/useRefreshHamburgNotifier'
import './DeviceTable.css'
import ModelPopup from '../../../../components/Modal'
import { CustomSwitch } from "../EditDevice/ParametersTable";
import { MemoTogglePolling } from "../TogglePolling";
import CustomToast from "../ToastComponent";
import { UpdateDeviceApiResponse, UpdateDevicePutRequest } from "../../services/updateDeviceService";
import { AssetCategoriesState } from "../../redux/assetCategoriesSlice";
import { UpdateDevicePollingApiResponse, UpdateDevicePollingPutRequest } from "../../services/updateDevicePollingService";

const ViewDeviceStrings = LanguageStrings().DeviceManagementCollectionStrings.ViewDevice
const DeviceManagementStrings = LanguageStrings().DeviceManagementCollectionStrings.TableComponent;
const httpStatus = APIConfiguration

export interface State extends SnackbarOrigin {
  open: boolean;
}

interface Data {
  name: string;
  device_type_id: string;
  asset_model_id: string;
  communication_status: string;
  hierarchy: string;
}

export type Order = "asc" | "desc";
interface HeadCell {
  disablePadding: boolean;
  id: any;
  label: string;
  numeric: boolean;
  sortable: boolean;
}

const headCells: readonly HeadCell[] = [
  {
    id: "name",
    numeric: false,
    disablePadding: false,
    label: DeviceManagementStrings.DeviceName,
    sortable: true,
  },
  {
    id: "device_type_id",
    numeric: false,
    disablePadding: false,
    label: DeviceManagementStrings.DeviceType,
    sortable: false,
  },
  {
    id: "communication_status",
    numeric: false,
    disablePadding: false,
    label: DeviceManagementStrings.CommunicationStatus,
    sortable: false,
  },
    {
    id: "device_Status",
    numeric: false,
    disablePadding: false,
    label: DeviceManagementStrings.PollingStatus,
    sortable: false,
  },
  {
    id: "hierarchy",
    numeric: false,
    disablePadding: false,
    label: DeviceManagementStrings.Hierarchy,
    sortable: false,
  },
  {
    id: "action",
    numeric: false,
    disablePadding: false,
    label: DeviceManagementStrings.Action,
    sortable: false,
  },
];

function EnhancedTableHead(props: any): any {
  const { order, orderBy, onRequestSort, deviceList } = props;
  const createSortHandler =
    (newOrderBy: keyof Data) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, newOrderBy);
    };
  // istanbul ignore else
  return (
    <TableHead data-testid="table-header">
      <TableRow>
        {headCells.map((headCell: any, index: number) => (
          <StyledTableCell
            key={headCell.id}
            align={
              /* istanbul ignore next */
              headCell.numeric ? "right" : "left"
            }
            padding={
              /* istanbul ignore next */
              headCell.disablePadding ? "none" : "normal"
            }
            sortDirection={
              /* istanbul ignore next */
              orderBy === headCell.id ? order : false
            }
            className={index === 0 ? 'sticky-col-0' : index === headCells.length - 1 ? 'sticky-col-action' : ''}
          >
            <TableSortLabel
              sx={{
                "& .MuiTableSortLabel-icon": {
                  opacity: 1
                }
              }}
              hideSortIcon={!headCell.sortable}
              direction={
                /* istanbul ignore next */
                orderBy === headCell.id ? order : "desc"
              }
              onClick={
                headCell.sortable ? createSortHandler(headCell.id) : undefined
              }
              aria-label={headCell.label}
              data-testid="test-sort"
            >
              {headCell.label}
              {
                /* istanbul ignore next */
                orderBy === headCell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === "desc" ? "sorted descending" : "sorted ascending"}
                  </Box>
                ) : null}
            </TableSortLabel>
          </StyledTableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    color: "#F0F0F0",
    fontWeight: "700",
    fontSize: 12,
    border: "2px solid #202020",
    padding: "5px 16px",
    fontFamily: "Honeywell Sans Web",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    color: "#d0d0d0",
    paddingLeft: "17px",
    borderBottom: "1px solid #202020",
    borderRight: 0,
    fontFamily: "Honeywell Sans Web",
  },
}));

export interface Sort {
  order: Order;
  orderBy: string;
}

function DeviceList(props: AllProps) {
  const navigate = useNavigate()
  const [confirmDeletePopup, setConfirmDeletePopup] = React.useState(false);
  const [selectedDeviceId, setSelectedDeviceId] = React.useState("");
  const [selectedDeviceName, setSelectedDeviceName] = React.useState("");
  const [showDeleteStatus, setShowDeleteStatus] = React.useState(false);
  const operationPermissions: OperationPermissionState = useSelector((state: RootState) => state.operationPermissions)
  const auth = useAuth()
  const { refreshHamburgAction } = useRefreshHamburgNotifier()
  const thermalCategory = 'Thermal'
  React.useEffect(() => {
    (!operationPermissions.updated && props.getOperationPermissions(auth.user?.profile.sub as string))
  }, [])

  const deviceDeleteStatus = useSelector(
    (state: RootState) => state.deviceDeleteState
  );
  const [open, setOpen] = React.useState<any>('')

  const wrapperRef = useRef(null);
  useOutsideAlerter(wrapperRef);

  function useOutsideAlerter(ref: any) {
  }

  React.useEffect(() => {
    /* istanbul ignore next */
    if (props.searchVal === "") {
      fetchAllAssetList();
    } else if (props.searchVal !== "") {
      props.performAction();
    }
  }, [props.page, props.sort]);

  const fetchAllAssetList = () => {
    // istanbul ignore else
    if (props.page >= 1) {
      props.fetchDeviceList(
        props.parentSiteId,
        props.assetTypeId,
        props.page,
        props.rowsPerPage,
        props.searchVal,
        props.sort.order,
        props.deviceAssetId,
        props.sort.orderBy
      );
    }
  };

  /* istanbul ignore next */
  const handleRequestSort =
  (
    event: React.MouseEvent<unknown>,
    newOrderBy: keyof Data
  ) => {
    /* istanbul ignore next */
    const isAsc =
      props.sort.orderBy === newOrderBy && props.sort.order === "asc";
    const toggledOrder = isAsc ? "desc" : "asc";
    props.setSort({ order: toggledOrder, orderBy: newOrderBy });
  };

  const formatNameWithTooltip = (text: string, length: number = 20): JSX.Element => {
    if (text?.length > length) {
      return (<Tooltip title={text}><span>{`${text.substring(0, length)}...`}</span></Tooltip>)
    }
    return <span>{text !== '' ? text : '-'}</span>
  }

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "& th": {
      border: "1px solid #202020",
    },
  }));

  const ViewDeviceOpen = (id: any): any => {
    setOpen(id)
  }

  const handlePollingToggle = (event: boolean, row: any) => {
    let asset_category_id: string = ""
    if (props?.assetCategories?.assetCategories != null && props?.assetCategories.assetCategories?.length > 0) {
      const thermalCat = props?.assetCategories.assetCategories.find((cat: any) => cat.category_type_name === thermalCategory)
      if (thermalCat != null) {
        asset_category_id = (thermalCat.category_type_id)
      }
    }
    
    props?.fetchDeviceInfo(row.id,(device: any) => {
      props.updateDevicePolling({ id: device.id, isPollingEnabled: event }, ()=>{
          CustomToast({
            type: 'success',
            heading: 'Device Status',
            message: `${event ? 'Enable' : 'Disable'} initiated successfully`
          })
          fetchAllAssetList()
        }, () =>{
          CustomToast({
              type: 'error',
              heading: 'Device Status',
              message: 'initiation failed'
          })
          fetchAllAssetList()
        })
    },()=>{
      CustomToast({
        type: 'error',
        heading: 'Device Status',
        message: 'Device Status update failed'
      })
      fetchAllAssetList()
    })
  }
  // istanbul ignore next
  const handleDeleteCampaignWithFailure = (): JSX.Element => {
    if (deviceDeleteStatus.httpStatus === httpStatus.SUCCESS_HTTP_STATUS) {
      return (
        <GenericModal
          buttonTypeNegative="hollow"
          title={DeviceManagementStrings.successfullyDeleted}
          LabelPositive="Okay"
          onClickPositive={() => {
            refreshHamburgAction('Device', 'Delete'), setShowDeleteStatus(false), fetchAllAssetList()
          }}
          onClose={() => {
            refreshHamburgAction('Device', 'Delete'), setShowDeleteStatus(false), fetchAllAssetList()
          }}
        >
          <p style={{ paddingTop: "1.2em" }}>
            {" "}
            {DeviceManagementStrings.FullMsgDeviceDeletedSuccessfully}{" "}
          </p>
        </GenericModal>
      );
    } else if (deviceDeleteStatus.isLoading) {
      return (
        <GenericModal title={"Loading..."}>
          <div className="CircularprogressAmi">
            <CircularProgress />
          </div>
        </GenericModal>
      );
    } else if (deviceDeleteStatus.httpStatus === httpStatus.BAD_REQUEST) {
      return (
        <GenericModal
          buttonTypeNegative="hollow"
          title={"Failed"}
          LabelPositive="Okay"
          onClickPositive={() => {
            setShowDeleteStatus(false);
          }}
          onClose={() => {
            setShowDeleteStatus(false);
          }}
        >
          <p style={{ paddingTop: "1.2em" }}> {deviceDeleteStatus.error} </p>
        </GenericModal>
      );
    } else if (deviceDeleteStatus.httpStatus === httpStatus.UNAUTHORIZED_HTTP_STATUS) {
      return (
        <div className="authError">
          <AuthError errorMessage="Unauthorized" />
        </div>
      );
    } else if (deviceDeleteStatus.httpStatus === httpStatus.ACCESS_FORBIDDEN_HTTP_STATUS) {
      return (
        <div className="authError">
          <AuthError errorMessage="accessForbidden" />
        </div>
      );
    } else {
      return (
        <GenericModal
          buttonTypeNegative="hollow"
          title={"Failed"}
          LabelPositive="Okay"
          onClickPositive={() => {
            setShowDeleteStatus(false);
          }}
          onClose={() => {
            setShowDeleteStatus(false);
          }}
        >
          <p style={{ paddingTop: "1.2em" }}> {deviceDeleteStatus.error} </p>
        </GenericModal>
      );
    }
  };

  // istanbul ignore else
  if (props.deviceList.deviceList !== null && props.deviceList.deviceCount > 0) {
    return (
      <>
        <Box className="devices">
          <Paper
            sx={{
              width: "100%",
              mb: 2,
              backgroundColor: "#272727",
              boxShadow: "unset",
              color: "#F0F0F0",
              marginBottom: "0",
            }}
          >
            <TableContainer className="device-table device-table-scroll">
              <Table
                sx={{ minWidth: 1200, color: "#f0f0f0" }}
                aria-labelledby="tableTitle"
                size={"small"}
              >
                <EnhancedTableHead
                  order={props.sort.order}
                  orderBy={props.sort.orderBy}
                  onRequestSort={handleRequestSort}
                  rowCount={props.deviceList.deviceList.length}
                  deviceList={props.deviceList.deviceList}
                  deviceListCount={props.deviceList.deviceList.length}
                />
                <TableBody>
                  {props.deviceList.deviceList &&
                    props.deviceList.deviceList.map((row: any, index: any) => {
                      const labelId = `enhanced-table-checkbox-${index}`;
                      return (
                        <StyledTableRow
                          hover
                          role="checkbox"
                          tabIndex={-1}
                          key={row.deviceId}
                          aria-label="devices-list-row"
                          sx={{ cursor: "pointer" }}
                          className={(!row.isPollingEnabled) ? 'online_polling': ''}
                        >
                          <StyledTableCell
                            component="th"
                            id={labelId}
                            scope="row"
                            padding="none"
                            aria-label="device-name"
                            data-testid="device-name"
                            sx={{ color: "#64C3FF !important", width: '16%', cursor: 'default !important' }}
                            className="sticky-col-0"
                          >
                            {formatNameWithTooltip(row?.name, 45)}
                          </StyledTableCell>
                          <StyledTableCell width={'280px'} sx={{ cursor: 'default !important' }}>{row.isCustomDevice ? row?.device_type : row?.device_type.replace('7800', row?.asset_model_name)}</StyledTableCell>
                          <StyledTableCell sx={{ width: '12%', cursor: 'default !important' }}>
                            <span className="communication_status" style={{ color: row.communication_status === "Online" ? "green" : "orangered"}}>
                              { formatNameWithTooltip(row?.communication_status !== "Offline" ? row?.communication_status : 'Offline') }
                            </span>
                          </StyledTableCell>
                          <StyledTableCell sx={{ width: '150px'}} >
                            {operationPermissions.permissions.can_modify_tiq_assets && (
                                <MemoTogglePolling onChange={(e: any) => handlePollingToggle(e, row) } checked={row?.isPollingEnabled} /> 
                            )}
                          </StyledTableCell>
                          <StyledTableCell sx={{ width: '25%', cursor: 'default !important' }}>{row?.hierarchy}</StyledTableCell>
                          <StyledTableCell sx={{ position: 'sticky',  right: 0, background: '#272727',  zIndex: 3 }} >
                            {operationPermissions.permissions.can_read_tiq_assets && (
                              <span style={{ color: "#64c3ff", textDecoration: "none", paddingLeft: "2px", marginRight: "15px" }}
                                onClick={() => ViewDeviceOpen(row.id)}
                                data-testid={'device-management-view_' + String(index)}
                              >
                                {DeviceManagementStrings.View}
                              </span>
                            )}
                            {operationPermissions.permissions.can_modify_tiq_assets && (
                              <span style={{ color: "#64c3ff", textDecoration: "none", paddingLeft: "2px", marginRight: "15px" }}
                                onClick={() => { navigate(`/edit-device/${row.id}`, { state: { isTcpProtocol: props.isTcpProtocol, parentAssetId: props.parentAssetId, parentSiteId: props.parentSiteId, deviceAssetId: props.deviceAssetId } }) }}
                                data-testid={'device-management-edittestimg' + String(index)}
                              >
                                {DeviceManagementStrings.Edit}
                              </span>
                            )}
                            {operationPermissions.permissions.can_delete_tiq_assets && (
                              <span style={{ color: "#64c3ff", textDecoration: "none", paddingLeft: "2px" }}
                                onClick={
                                  () => {
                                    setSelectedDeviceId(row.id);
                                    setSelectedDeviceName(row.name.length > 20 ? `${row.name.substring(0, 20)}...` : row.name);
                                    setConfirmDeletePopup(true);
                                  }}
                                data-testid={'device-management-delete_' + String(index)}
                              >
                                {DeviceManagementStrings.Delete}
                              </span>
                            )}
                          </StyledTableCell>
                        </StyledTableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
            <Pagination
              count={props.deviceList.deviceCount}
              page={props.page}
              setPage={props.setPage}
              rowsPerPage={props.rowsPerPage}
              siblingCount={1}
              boundaryCount={1}
            />
          </Paper>
        </Box>
        {open !== '' && (
          <>
             <ModelPopup
                      title={ViewDeviceStrings.DeviceInformation}
                      sx={{ 
                        width: { xs: '95%',  md: '90%', lg: '90%', xl: '80%' },
                        height: { xs: '95%',  md: '80%' } 
                      }}
                      className= 'viewDevice'
                      onClose={() => {
                        setOpen('')
                      }}
                    >
                      
                    <ViewDevice viewDevicePopup={
              (params?: any) => { setOpen(params) }} data-testid="viewdevice_chk" deviceId={open}
            /> 
                    </ModelPopup>
          </>
        )}
        {confirmDeletePopup && (
          <GenericModal
            buttonTypeNegative="hollow"
            title={`${DeviceManagementStrings.Delete} ${selectedDeviceName}`}
            LabelPositive="Confirm"
            LabelNegative="Cancel"
            height="250px"
            onClickPositive={
              () => {
                setConfirmDeletePopup(false);
                setShowDeleteStatus(true);
                props.deviceDeleteCampaign(selectedDeviceId,auth.user?.profile.sub ?? '');
              }}
            onClose={
              () => {
                setConfirmDeletePopup(false);
              }}
            onClickNegative={
              () => {
                setConfirmDeletePopup(false);
              }}
          >
            <p style={{ paddingTop: "1.2em" }}>
              {DeviceManagementStrings.DeleteConfirmation}
              <br/><br/>
              {DeviceManagementStrings.DeviceDeleteMessage}
            </p>
          </GenericModal>
        )}
        {
          showDeleteStatus && handleDeleteCampaignWithFailure()
        }
      </>
    );
  } else if (props.deviceList.isLoading) {
    return (
      <div className="CircularprogressAmi" data-testid="loading-spinner">
        <CircularProgress />
      </div>
    );
  } else if (props.deviceList.httpStatus === httpStatus.UNAUTHORIZED_HTTP_STATUS) {
    return (
      <div className="authError">
        <AuthError errorMessage="Unauthorized" />
      </div>
    );
  } else if (props.deviceList.httpStatus === httpStatus.ACCESS_FORBIDDEN_HTTP_STATUS) {
    return (
      <div className="authError">
        <AuthError errorMessage="accessForbidden" />
      </div>
    );
  } else if (
    props.deviceList.deviceList !== null &&
    props.deviceList.deviceList !== undefined &&
    props.deviceList.deviceList.length === 0
  ) {
    return (
      <div className="authError">
        <AuthError errorMessage="NoDataPresent" />
      </div>
    );
  } else if (
    props.deviceList.loadingError?.includes(DeviceManagementStrings.NoAssetsFound) ||
    props.deviceList.loadingError?.includes(DeviceManagementStrings.AssetNotFound)
  ) {
    return (
      <div className="authError">
        <AuthError errorMessage="NoDataPresent" />
      </div>
    );
  } else {
    return (
      <div className="authError">
        <AuthError errorMessage="cannotFetch" retry={fetchAllAssetList} />
      </div>
    );
  }
}

interface DispatchToProps {
  fetchDeviceList: (
    parentSiteId: string,
    assetTypeId: string,
    PageNumber: number,
    PageItems: number,
    searchKey: string,
    SortParams: string,
    parentAssetId: string,
    sortField: string
  ) => void;
  deviceDeleteCampaign: (selectedDeviceId: string, subId: string) => void;
  getOperationPermissions: (sub: string) => void
  updateDevicePolling: (updateDevicePollingPutRequest: UpdateDevicePollingPutRequest,
    onSuccess: (successResponse: UpdateDevicePollingApiResponse, httpStatus: number) => any,
    onFailure: (errorMessage: any, httpStatus: number) => any
  ) => void
  fetchDeviceInfo: (deviceTypeId: string, 
    onSuccess?: (device: any, httpStatus: number) => any,
    onFailure?: (errorMessage: any, httpStatus: number) => any) => void
}
const mapDispatchToProps = (dispatch: any): DispatchToProps => ({
  fetchDeviceList: (
    parentSiteId: string,
    assetTypeId: string,
    PageNumber: number,
    PageItems: number,
    searchKey: string,
    SortParams: string,
    parentAssetId: string,
    sortField: string
  ) =>
    dispatch(
      fetchDeviceList(
        parentSiteId,
        assetTypeId,
        PageNumber,
        PageItems,
        searchKey,
        SortParams,
        parentAssetId,
        sortField
      )
    ),
  deviceDeleteCampaign: (selectedDeviceId: string, subId: string) => dispatch(deviceDeleteCampaign(selectedDeviceId, subId)),
  getOperationPermissions: (sub: string) => dispatch(getOperationPermissions(sub)),
  fetchDeviceInfo: (deviceTypeId: string, 
    onSuccess?: (device: any, httpStatus: number) => any,
    onFailure?: (errorMessage: any, httpStatus: number) => any) => dispatch(fetchDeviceInfo(deviceTypeId,onSuccess,onFailure)),
  updateDevicePolling: (updateDevicePollingPutRequest: UpdateDevicePollingPutRequest, 
    onSuccess: (successResponse: UpdateDevicePollingApiResponse, httpStatus: number) => any,
    onFailure: (errorMessage: any, httpStatus: number) => any) => dispatch(updateDevicePolling(updateDevicePollingPutRequest, onSuccess, onFailure)), 
  })
interface StateToProps {
  deviceList: DeviceListState
  assetCategories: AssetCategoriesState
}
const mapStateToProps = (state: RootState): StateToProps => ({
  deviceList: state.deviceManagementDeviceList,
  assetCategories: state.assetCategories,
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface ParentProps {
  isTcpProtocol: boolean;
  assetTypeId: string;
  parentAssetId: string;
  parentSiteId: string;
  deviceAssetId: string;
  searchVal: string;
  performAction: () => void;
  rowsPerPage: number;
  page: number;
  sort: Sort;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  setSort: React.Dispatch<React.SetStateAction<Sort>>;
}

type AllProps = PropsFromRedux & ParentProps;

export default connector(DeviceList);